import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import companyConfig from '../config/companyConfig';

function Career() {
  const [savedJobs, setSavedJobs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const recaptchaRef = useRef(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    resume: null,
    portfolioWebsite: '',
    githubProfile: '',
    linkedinProfile: '',
    behanceProfile: '',
    otherLinks: '',
    coverLetter: ''
  });

  const stats = [
    { number: '50+', label: 'Global Clients' },
    { number: '95%', label: 'Employee Satisfaction' },
    { number: '25+', label: 'Countries Served' },
    { number: '4.8', label: 'Glassdoor Rating' }
  ];

  // Jobs array - Uncomment the positions below when hiring
  const jobs = [
    // {
    //   id: 1,
    //   title: 'Senior Cloud Architect',
    //   location: 'Remote',
    //   type: 'Full-time',
    //   description: 'Lead the design and implementation of scalable cloud infrastructure solutions for enterprise clients. Work with cutting-edge technologies and drive digital transformation initiatives.',
    //   requirements: [
    //     '8+ years cloud architecture experience',
    //     'Expertise with AWS / Azure / GCP',
    //     'Strong DevOps background',
    //     'Enterprise-level project leadership'
    //   ]
    // },
    // {
    //   id: 2,
    //   title: 'Cybersecurity Engineer',
    //   location: 'New York',
    //   type: 'Full-time',
    //   description: 'Protect enterprise systems and data through advanced security solutions. Design and implement security protocols, conduct vulnerability assessments, and lead incident response.',
    //   requirements: [
    //     '5+ years cybersecurity experience',
    //     'CISSP or equivalent certification',
    //     'Expertise in threat detection',
    //     'Strong analytical skills'
    //   ]
    // },
    // {
    //   id: 3,
    //   title: 'SAP Consultant',
    //   location: 'Hybrid',
    //   type: 'Full-time',
    //   description: 'Guide clients through SAP implementations and optimizations. Leverage deep functional knowledge to deliver business transformation and process improvement.',
    //   requirements: [
    //     '6+ years SAP consulting experience',
    //     'S/4HANA expertise preferred',
    //     'Strong client relationship skills',
    //     'Business process optimization'
    //   ]
    // },
    // {
    //   id: 4,
    //   title: 'Data Analytics Lead',
    //   location: 'Remote',
    //   type: 'Full-time',
    //   description: 'Transform raw data into actionable insights for Fortune 500 clients. Build advanced analytics solutions and lead a team of data scientists and engineers.',
    //   requirements: [
    //     '7+ years analytics experience',
    //     'Python, R, SQL proficiency',
    //     'Machine learning expertise',
    //     'Team leadership experience'
    //   ]
    // }
  ];

  const benefits = [
    {
      icon: 'fa-heart-pulse',
      title: 'Health & Wellness',
      perks: [
        'Comprehensive medical, dental, vision',
        'Mental health support programs',
        'Gym membership reimbursement',
        'Wellness coaching'
      ]
    },
    {
      icon: 'fa-chart-line',
      title: 'Career Growth',
      perks: [
        'Professional development budget',
        'Certification sponsorship',
        'Mentorship programs',
        'Internal mobility opportunities'
      ]
    },
    {
      icon: 'fa-balance-scale',
      title: 'Work-Life Balance',
      perks: [
        'Flexible working hours',
        'Remote work options',
        'Generous PTO policy',
        'Paid parental leave'
      ]
    },
    {
      icon: 'fa-piggy-bank',
      title: 'Financial Benefits',
      perks: [
        'Competitive salary packages',
        '401(k) with company match',
        'Performance bonuses',
        'Stock options'
      ]
    },
    {
      icon: 'fa-laptop-code',
      title: 'Technology',
      perks: [
        'Latest hardware & software',
        'Home office setup stipend',
        'Innovation time allocation',
        'Tech conference attendance'
      ]
    },
    {
      icon: 'fa-users',
      title: 'Team Culture',
      perks: [
        'Collaborative environment',
        'Team building events',
        'Diversity & inclusion programs',
        'Global networking opportunities'
      ]
    },
    {
      icon: 'fa-graduation-cap',
      title: 'Learning & Development',
      perks: [
        'Online course subscriptions',
        'Technical training programs',
        'Leadership development',
        'Industry certifications'
      ]
    },
    {
      icon: 'fa-gift',
      title: 'Additional Perks',
      perks: [
        'Commuter benefits',
        'Employee referral bonuses',
        'Company-sponsored events',
        'Anniversary rewards'
      ]
    }
  ];

  const toggleSaveJob = (jobId) => {
    setSavedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const scrollToPositions = () => {
    document.getElementById('open-positions')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setFormData(prev => ({ ...prev, position: job.title }));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
    setRecaptchaValue(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      position: '',
      experience: '',
      resume: null,
      portfolioWebsite: '',
      githubProfile: '',
      linkedinProfile: '',
      behanceProfile: '',
      otherLinks: '',
      coverLetter: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 10 * 1024 * 1024) { // 10MB limit
      setFormData(prev => ({ ...prev, resume: file }));
    } else {
      alert('File size must be less than 10MB');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate reCAPTCHA (only if enabled)
    if (companyConfig.recaptcha.enabled !== false && !recaptchaValue) {
      alert('Please complete the reCAPTCHA verification to submit your application.');
      return;
    }
    
    alert('Application submitted successfully!');
    closeModal();
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  return (
    <div className="career-page">
      {/* Hero Section */}
      <section className="career-hero">
        <div className="career-hero-overlay"></div>
        <div className="career-hero-content">
          <h1 className="career-hero-heading">
            Join <span className="highlight-blue">Our</span> Team
          </h1>
          <p className="career-hero-subtext">
            Build your career with a global leader in IT, cloud, and consulting services.
            Discover opportunities to make an impact and lead the future of technology.
          </p>
          <div className="career-hero-buttons">
            <button className="career-btn-primary" onClick={scrollToPositions}>
              Open Positions
            </button>
            <Link to="/career/info" className="career-btn-secondary">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="career-stats-section">
        <div className="career-stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="career-stat-pill">
              <span className="career-stat-number">{stat.number}</span>
              <span className="career-stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="career-positions-section" id="open-positions">
        <div className="career-positions-container">
          <div className="career-section-header">
            <h2 className="career-section-title">Open Positions</h2>
            <p className="career-section-subtitle">
              Explore exciting opportunities to advance your career and make an impact.
            </p>
          </div>

          {/* Job Cards Grid or Empty State */}
          {jobs.length === 0 ? (
            <div className="no-positions-message">
              <div className="no-positions-icon">
                <i className="fas fa-briefcase"></i>
              </div>
              <h3 className="no-positions-title">No Positions Currently Available</h3>
              <p className="no-positions-text">
                We don't have any open positions at the moment. Please check back later or feel free to submit your resume for future opportunities.
              </p>
              <p className="no-positions-subtext">
                Follow us on our social media channels to stay updated on new job openings!
              </p>
            </div>
          ) : (
            <div className="career-jobs-grid">
              {jobs.map((job) => (
                <div key={job.id} className="career-job-card">
                  <div className="job-card-header">
                    <h3 className="job-card-title">{job.title}</h3>
                    <div className="job-card-tags">
                      <span className="job-tag">{job.location}</span>
                      <span className="job-tag">{job.type}</span>
                    </div>
                  </div>
                  
                  <p className="job-card-description">{job.description}</p>
                  
                  <div className="job-card-requirements">
                    <ul>
                      {job.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="job-card-actions">
                    <button 
                      className="job-apply-btn"
                      onClick={() => handleApplyClick(job)}
                    >
                      Apply Now
                    </button>
                  <button 
                    className={`job-save-btn ${savedJobs.includes(job.id) ? 'saved' : ''}`}
                    onClick={() => toggleSaveJob(job.id)}
                    aria-label={savedJobs.includes(job.id) ? 'Unsave job' : 'Save job'}
                  >
                    <i className={`fas fa-bookmark${savedJobs.includes(job.id) ? '' : '-o'}`}></i>
                    {savedJobs.includes(job.id) ? 'Saved' : 'Save'}
                  </button>
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* Benefits & Perks Section */}
      <section className="career-benefits-section">
        <div className="career-benefits-container">
          <div className="career-section-header">
            <h2 className="career-section-title">Benefits & Perks</h2>
            <p className="career-section-subtitle">
              We invest in our people with comprehensive benefits and a supportive work environment.
            </p>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon-circle">
                  <i className={`fas ${benefit.icon}`}></i>
                </div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <ul className="benefit-perks-list">
                  {benefit.perks.map((perk, perkIndex) => (
                    <li key={perkIndex}>{perk}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Application Modal */}
      {isModalOpen && (
        <div className="application-modal-overlay" onClick={closeModal}>
          <div className="application-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header">
              <h2 className="modal-title">Apply for a Position</h2>
              <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="application-form">
              {/* Section 1: Personal Details */}
              <div className="form-section">
                <h3 className="section-title">Personal Details</h3>
                <div className="section-divider"></div>
                
                <div className="form-grid-2col">
                  <div className="form-field">
                    <label htmlFor="fullName" className="form-label">Full Name *</label>
                    <div className="input-with-icon">
                      <i className="fas fa-user input-icon"></i>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        className="form-input"
                        autoComplete="name"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <div className="input-with-icon">
                      <i className="fas fa-envelope input-icon"></i>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter your email address"
                        className="form-input"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="phone" className="form-label">Phone Number *</label>
                    <div className="input-with-icon">
                      <i className="fas fa-phone input-icon"></i>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Enter your phone number"
                        className="form-input"
                        autoComplete="tel"
                        required
                      />
                    </div>
                    <small style={{color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block'}}>
                      <i className="fas fa-info-circle"></i> Include country code (e.g., +1, +91)
                    </small>
                  </div>

                  <div className="form-field">
                    <label htmlFor="position" className="form-label">Position Applied For *</label>
                    <div className="input-with-icon">
                      <i className="fas fa-briefcase input-icon"></i>
                      <input
                        type="text"
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="e.g., SAP Consultant, Cloud Engineer"
                        autoComplete="organization-title"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-field form-field-full">
                    <label htmlFor="experience" className="form-label">Years of Experience *</label>
                    <div className="input-with-icon">
                      <i className="fas fa-chart-line input-icon"></i>
                      <input
                        type="text"
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="e.g., 5 years, 10+ years"
                        autoComplete="off"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Professional Portfolio & Documents */}
              <div className="form-section">
                <h3 className="section-title">Professional Portfolio & Documents</h3>
                <div className="section-divider"></div>

                {/* Resume Upload */}
                <div className="form-field">
                  <label htmlFor="resume" className="form-label">Resume/CV *</label>
                  <div className="file-upload-wrapper">
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx"
                      className="file-input"
                      required
                    />
                    <label htmlFor="resume" className="file-upload-label">
                      <i className="fas fa-cloud-upload-alt"></i>
                      <span className="file-upload-text">
                        {formData.resume ? formData.resume.name : 'Choose File'}
                      </span>
                      <span className="file-upload-btn">Browse</span>
                    </label>
                  </div>
                  <p className="file-upload-note">
                    Accepted formats: PDF, DOC, DOCX (Max 10MB)
                  </p>
                </div>

                {/* Portfolio & Social Links */}
                <div className="form-grid-2col">
                  <div className="form-field">
                    <label htmlFor="portfolioWebsite" className="form-label">Portfolio Website</label>
                    <div className="input-with-icon">
                      <i className="fas fa-link input-icon"></i>
                      <input
                        type="url"
                        id="portfolioWebsite"
                        name="portfolioWebsite"
                        value={formData.portfolioWebsite}
                        onChange={handleInputChange}
                        placeholder="https://yourportfolio.com"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="githubProfile" className="form-label">GitHub Profile</label>
                    <div className="input-with-icon">
                      <i className="fab fa-github input-icon"></i>
                      <input
                        type="url"
                        id="githubProfile"
                        name="githubProfile"
                        value={formData.githubProfile}
                        onChange={handleInputChange}
                        placeholder="https://github.com/username"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="linkedinProfile" className="form-label">LinkedIn Profile</label>
                    <div className="input-with-icon">
                      <i className="fab fa-linkedin input-icon"></i>
                      <input
                        type="url"
                        id="linkedinProfile"
                        name="linkedinProfile"
                        value={formData.linkedinProfile}
                        onChange={handleInputChange}
                        placeholder="https://linkedin.com/in/user"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="behanceProfile" className="form-label">Behance Profile</label>
                    <div className="input-with-icon">
                      <i className="fab fa-behance input-icon"></i>
                      <input
                        type="url"
                        id="behanceProfile"
                        name="behanceProfile"
                        value={formData.behanceProfile}
                        onChange={handleInputChange}
                        placeholder="https://behance.net/username"
                        className="form-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="otherLinks" className="form-label">Other Professional Links</label>
                  <input
                    type="text"
                    id="otherLinks"
                    name="otherLinks"
                    value={formData.otherLinks}
                    onChange={handleInputChange}
                    placeholder="Include any other relevant professional profile, personal website, or project links."
                    className="form-input"
                  />
                </div>
              </div>

              {/* Section 3: Cover Letter */}
              <div className="form-section">
                <h3 className="section-title">Cover Letter</h3>
                <div className="section-divider"></div>

                <div className="form-field">
                  <label htmlFor="coverLetter" className="form-label">
                    Why are you interested in this position? *
                  </label>
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    placeholder="Tell us about your interest in this role, relevant experience and what you can bring to our team..."
                    className="form-textarea"
                    rows="6"
                    required
                  ></textarea>
                  <small style={{color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block'}}>
                    <i className="fas fa-info-circle"></i> Please provide at least 15 words for better clarity
                  </small>
                </div>
              </div>

              {/* reCAPTCHA Section */}
              {companyConfig.recaptcha.enabled !== false && (
              <div className="form-section recaptcha-section">
                <div className="recaptcha-wrapper">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={companyConfig.recaptcha.siteKey}
                    onChange={handleRecaptchaChange}
                    theme="light"
                  />
                </div>
                <p className="recaptcha-note">
                  <i className="fas fa-shield-alt"></i>
                  Protected by reCAPTCHA. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms</a> apply.
                </p>
              </div>
              )}

              {/* Modal Actions */}
              <div className="modal-actions">
                <button type="button" className="modal-btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="modal-btn-submit">
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Career;
